import React from 'react'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import {
  faCheck,
  faIdBadge,
  faTruck,
  faX,
} from '@fortawesome/pro-light-svg-icons'
import { faCircleCheck } from '@fortawesome/free-regular-svg-icons'
import { ImportsReadModel } from '../../model/imports.state.types'
import { TaskModel } from '@/app/models/tasks/taskModel'
import { FaIcon, faTriangleExclamationRegular } from '@/lib/fa'
import { Button } from '@/ui'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { LoaderIcon } from '@/app/features/icons/Loader'
import { ImportSummarySnapshot } from '../../container/import-popup-container/useImportFlow'

type Props = {
  uploadDetails: ImportsReadModel
  task: TaskModel
  handleSubmit: () => void
  summarySnapshot?: ImportSummarySnapshot
  /** Odczyt po zapisie - ze stanem kierowców i pojazdów w bazie. */
  importResult?: ImportsReadModel
  isResultPending?: boolean
}

type SavedStatus = 'active' | 'inactive' | 'notSaved'

type SummaryRow = {
  key: string
  label: string
  cardNumber?: string
  status: SavedStatus
  /** Zaznaczone do aktywacji, a w bazie nie jest aktywne. */
  mismatch: boolean
}

const STATUS_ORDER: Record<SavedStatus, number> = {
  active: 0,
  inactive: 1,
  notSaved: 2,
}

const savedStatusOf = (
  states: Record<string, boolean>,
  key: string,
): SavedStatus =>
  key in states ? (states[key] ? 'active' : 'inactive') : 'notSaved'

const sortRows = (rows: SummaryRow[]) =>
  [...rows].sort(
    (a, b) =>
      Number(b.mismatch) - Number(a.mismatch) ||
      STATUS_ORDER[a.status] - STATUS_ORDER[b.status] ||
      a.label.localeCompare(b.label),
  )

function uniqueBy<T>(items: T[], keyOf: (item: T) => string): T[] {
  const seen = new Set<string>()
  return items.filter((item) => {
    const key = keyOf(item)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

type TableProps = {
  title: string
  icon: IconDefinition
  nameLabel: string
  rows: SummaryRow[]
  showCardNumber?: boolean
}

function SummaryTable({ title, icon, nameLabel, rows, showCardNumber }: TableProps) {
  const { t } = useLocalize()
  const gridClass = showCardNumber
    ? 'tw-grid tw-grid-cols-[1.4fr,1.4fr,1fr] tw-items-center tw-gap-2'
    : 'tw-grid tw-grid-cols-[1fr,1.4fr] tw-items-center tw-gap-2'

  const renderStatus = (row: SummaryRow) => {
    const status =
      row.status === 'active' ? (
        <span className="tw-text-success">
          <FaIcon icon={faCheck} /> {t('common.active')}
        </span>
      ) : row.status === 'inactive' ? (
        <span>
          <FaIcon icon={faX} /> {t('common.inactive')}
        </span>
      ) : (
        <span className="tw-text-error">
          <FaIcon icon={faX} /> {t('importModule.not-saved')}
        </span>
      )

    return (
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-3">
        {status}
        {row.mismatch && (
          <span className="tw-text-xs tw-text-warning">
            <FaIcon icon={faTriangleExclamationRegular} />{' '}
            {t('importModule.summary-selected-not-active')}
          </span>
        )}
      </div>
    )
  }

  return (
    <div>
      <h4 className="tw-mb-2 tw-text-sm tw-font-semibold tw-text-slate-600">
        <FaIcon icon={icon} className="tw-mr-1" />
        {title} ({rows.length})
      </h4>
      <div className="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white">
        <div
          className={`${gridClass} tw-border-b tw-border-slate-200 tw-bg-slate-50 tw-px-4 tw-py-2 tw-text-xs tw-font-medium tw-text-slate-500`}
        >
          <div>{nameLabel}</div>
          <div>{t('importModule.col-db-status')}</div>
          {showCardNumber && <div>{t('importModule.col-card-number')}</div>}
        </div>
        {rows.map((row) => (
          <div
            key={row.key}
            className={`${gridClass} tw-px-4 tw-py-2.5 tw-text-sm hover:tw-bg-slate-50 ${
              row.mismatch ? 'tw-bg-content-warning' : ''
            }`}
          >
            <div>{row.label}</div>
            {renderStatus(row)}
            {showCardNumber && <div>{row.cardNumber}</div>}
          </div>
        ))}
      </div>
    </div>
  )
}

export function PopupStepImportSummary(props: Props) {
  const { t } = useLocalize()
  const { uploadDetails, summarySnapshot, importResult } = props
  const dbStates = importResult?.dbStates
  const filesCount = Object.values(uploadDetails.filesById).length

  const drivers = uniqueBy(
    Object.values(uploadDetails.driverItemsById),
    (d) => d.cardNumber,
  )
  const allVehicles = uniqueBy(
    Object.values(uploadDetails.vehicleItemsById),
    (v) => v.registrationNumber,
  )
  const vehicles = summarySnapshot
    ? allVehicles.filter((v) =>
        summarySnapshot.filteredVehicleRegistrationNumbers.includes(
          v.registrationNumber,
        ),
      )
    : allVehicles

  const selectedDrivers = summarySnapshot?.selectedDriverCardNumbers
  const selectedVehicles = summarySnapshot?.selectedVehicleRegistrationNumbers

  // Bez stanu z serwera (starsze API) zostaje wnioskowanie z zaznaczenia.
  const driverRows = sortRows(
    drivers.map((d) => {
      const selected = selectedDrivers?.includes(d.cardNumber)
      const status: SavedStatus = dbStates
        ? savedStatusOf(dbStates.drivers, d.cardNumber)
        : (selected ?? d.bucket !== 'ignored')
          ? 'active'
          : 'inactive'
      return {
        key: d.cardNumber,
        label: d.fullName,
        cardNumber: d.cardNumber,
        status,
        mismatch: Boolean(dbStates && selected && status !== 'active'),
      }
    }),
  )

  const vehicleRows = sortRows(
    vehicles.map((v) => {
      const selected = selectedVehicles?.includes(v.registrationNumber)
      const status: SavedStatus = dbStates
        ? savedStatusOf(dbStates.vehicles, v.registrationNumber)
        : (selected ?? v.bucket !== 'ignored')
          ? 'active'
          : 'inactive'
      return {
        key: v.registrationNumber,
        label: v.registrationNumber,
        status,
        mismatch: Boolean(dbStates && selected && status !== 'active'),
      }
    }),
  )

  const countActive = (rows: SummaryRow[]) =>
    rows.filter((r) => r.status === 'active').length
  const mismatches = [...driverRows, ...vehicleRows].filter(
    (r) => r.mismatch,
  ).length

  if (props.isResultPending) {
    return (
      <div className="tw-grid tw-h-40 tw-place-items-center">
        <LoaderIcon className="tw-w-10 tw-animate-spin" />
      </div>
    )
  }

  return (
    <div className="tw-flex tw-h-fit tw-max-h-[80vh] tw-flex-col">
      <div className="tw-jusitfy-start tw-border-border-base tw-flex tw-w-full tw-flex-col tw-gap-2 tw-border-b tw-pb-4">
        <div className="tw-flex tw-items-center tw-gap-2">
          <FaIcon
            icon={mismatches > 0 ? faTriangleExclamationRegular : faCircleCheck}
            className={`tw-h-5 tw-w-5 ${mismatches > 0 ? 'tw-text-warning' : 'tw-text-success'}`}
          />
          <h3 className="tw-text-lg tw-font-medium tw-text-content-base">
            {t('importModule.imported-files-count').replace('{0}', String(filesCount))}
          </h3>
        </div>
        <p>
          {t('importModule.summary-active-in-db')}{' '}
          <b>{countActive(driverRows)}</b>/{driverRows.length} {t('importModule.drivers-and')}{' '}
          <b>{countActive(vehicleRows)}</b>/{vehicleRows.length} {t('importModule.vehicles-dot')}
        </p>
        {mismatches > 0 && (
          <p className="tw-rounded-md tw-border tw-border-warning/30 tw-bg-content-warning tw-px-3 tw-py-2 tw-text-sm tw-text-warning">
            <FaIcon icon={faTriangleExclamationRegular} />{' '}
            {t('importModule.summary-mismatch').replace('{0}', String(mismatches))}
          </p>
        )}
      </div>
      <div className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-py-4">
        <div className="tw-grid tw-items-start tw-gap-4 lg:tw-grid-cols-[3fr,2fr]">
          {driverRows.length > 0 && (
            <SummaryTable
              title={t('importPanel.drivers')}
              icon={faIdBadge}
              nameLabel={t('importModule.col-name')}
              rows={driverRows}
              showCardNumber
            />
          )}
          {vehicleRows.length > 0 && (
            <SummaryTable
              title={t('importPanel.vehicles')}
              icon={faTruck}
              nameLabel={t('importModule.col-registration')}
              rows={vehicleRows}
            />
          )}
        </div>
      </div>
      <div className="tw-border-border-base tw-flex tw-justify-end tw-border-t tw-pt-4">
        <Button
          className="tw-h-[38px] tw-w-[102px]"
          text="OK"
          onClick={props.handleSubmit}
        />
      </div>
    </div>
  )
}
